/**
 * useAgentContext Hook
 * 
 * Hook que captura o contexto da página atual para enviar ao agente ChatKit.
 * Usa useSelector + useMemo para garantir atualizações reativas do Redux.
 * 
 * Funcionalidade:
 * - Detecta a página atual (dashboard, premissas, dre-editavel)
 * - Serializa dados relevantes do Redux state
 * - Recalcula automaticamente quando state muda
 * - Retorna objeto pronto para envio ao agente
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '../store';

/**
 * Estrutura de contexto enviado ao agente
 */
export interface AgentPageContext {
  page: string; // dashboard | premissas | dre-editavel | other
  timestamp: string;
  currentScenario?: string;
  cards?: {
    label: string;
    value: string | number;
  }[];
  assumptions?: {
    setima?: unknown;
    studios555?: unknown;
  };
  dre?: {
    baseYear?: number;
    editedLines?: unknown;
  };
}

/**
 * Hook principal que retorna contexto da página atual
 */
export function useAgentContext(): AgentPageContext {
  const location = useLocation();
  const valuationState = useSelector((state: RootState) => state.valuation);

  // useMemo com dependencies garante recálculo quando state muda
  const pageContext = useMemo(() => {
    const pathname = location.pathname;
    let page = 'other';
    
    // Detecta a página atual
    if (pathname === '/' || pathname === '/dashboard') {
      page = 'dashboard';
    } else if (pathname.includes('/premissas')) {
      page = 'premissas';
    } else if (pathname.includes('/dre')) {
      page = 'dre-editavel';
    }

    const context: AgentPageContext = {
      page,
      timestamp: new Date().toISOString(),
    };

    // ========================================================================
    // DASHBOARD - Adiciona cards principais (EV, Valuation, etc)
    // ========================================================================
    if (page === 'dashboard') {
      const snapshot = valuationState.snapshot;
      const currentScenario = valuationState.currentScenario;
      
      context.currentScenario = currentScenario;
      context.cards = [];

      if (snapshot) {
        // Card: Enterprise Value Total
        const evTotal = snapshot.consolidado.EV_Total;
        context.cards.push({
          label: 'Enterprise Value Total',
          value: `R$ ${evTotal.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
        });

        // Card: Equity Value
        const equity = snapshot.consolidado.Equity;
        context.cards.push({
          label: 'Equity Value',
          value: `R$ ${equity.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
        });

        // Card: Equity Ajustado (após descontos)
        const equityAjustado = snapshot.consolidado.Equity_Ajustado;
        context.cards.push({
          label: 'Equity Ajustado',
          value: `R$ ${equityAjustado.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
        });

        // Card: WACC Mix
        const waccMix = snapshot.consolidado.WACC_Mix * 100;
        context.cards.push({
          label: 'WACC Mix',
          value: `${waccMix.toFixed(2)}%`
        });

        // Adiciona valores por empresa
        context.cards.push({
          label: 'Sétima - EV',
          value: `R$ ${snapshot.setimaValuation.ev.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
        });

        context.cards.push({
          label: '555 Studios - EV',
          value: `R$ ${snapshot.studios555Valuation.ev.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
        });

        // Valor da Participação
        context.cards.push({
          label: 'Valor da Participação',
          value: `R$ ${snapshot.valorParticipacao.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
        });
      }
    }

    // ========================================================================
    // PREMISSAS - Adiciona premissas das empresas
    // ========================================================================
    if (page === 'premissas') {
      const currentScenario = valuationState.currentScenario;
      context.currentScenario = currentScenario;
      
      // Serializa premissas
      const setimaParams = valuationState.companyParams.setima;
      const studios555Params = valuationState.companyParams.studios555;

      // Adiciona cenários disponíveis
      const scenarios = valuationState.scenarios;
      if (scenarios) {
        context.assumptions = {
          setima: {
            params: setimaParams,
            cenarios: {
              pessimista: scenarios.setima.pessimista,
              base: scenarios.setima.base,
              otimista: scenarios.setima.otimista,
            }
          },
          studios555: {
            params: studios555Params,
            cenarios: {
              pessimista: scenarios.studios555.pessimista,
              base: scenarios.studios555.base,
              otimista: scenarios.studios555.otimista,
            }
          }
        };
      }
    }

    // ========================================================================
    // DRE EDITÁVEL - Adiciona dados da DRE
    // ========================================================================
    if (page === 'dre-editavel') {
      // Por enquanto, apenas indicamos que estamos na página de DRE
      // No futuro, podemos adicionar dados editados da DRE quando tivermos o slice
      context.dre = {
        baseYear: valuationState.baseYear,
      };
    }

    return context;
  }, [
    location.pathname,
    valuationState.snapshot,
    valuationState.currentScenario,
    valuationState.scenarios,
    valuationState.companyParams,
    valuationState.baseYear,
  ]);

  return pageContext;
}
